import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
    CpuChipIcon,
    ChartBarIcon,
    BeakerIcon,
    ShieldCheckIcon,
    CloudIcon,
    GlobeAltIcon,
    SparklesIcon,
    ArchiveBoxIcon,
    ArrowRightIcon
} from '@heroicons/react/24/outline';
import SEO from '../../components/SEO';

const Services = () => {
    const fadeInUp = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
    };

    const servicesList = [
        {
            title: "Smart Farming Solutions",
            desc: "Data-driven farming decisions, crop-specific planning, input optimization, and yield forecasting.",
            icon: <CpuChipIcon className="w-8 h-8" />,
            link: "/services/precision-agriculture",
            color: "bg-blue-500"
        },
        {
            title: "Crop Advisory & Agronomy",
            desc: "Crop selection & planning, stage-wise nutrient management, growth monitoring, and stress diagnosis.",
            icon: <ChartBarIcon className="w-8 h-8" />,
            link: "/services/expert-consultancy",
            color: "bg-green-500"
        },
        {
            title: "Soil Health & Nutrition",
            desc: "Soil testing & interpretation, balanced macronutrient plans, organic solutions, and fertility improvement.",
            icon: <BeakerIcon className="w-8 h-8" />,
            link: "/services/expert-consultancy",
            color: "bg-amber-500"
        },
        {
            title: "Pest & Disease Management",
            desc: "Integrated Pest Management (IPM), biological solutions, and resistance management strategies.",
            icon: <ShieldCheckIcon className="w-8 h-8" />,
            link: "/services/crop-protection",
            color: "bg-red-500"
        },
        {
            title: "Water & Irrigation",
            desc: "Crop-wise water requirement planning, drip & sprinkler optimization, and climate-smart practices.",
            icon: <CloudIcon className="w-8 h-8" />,
            link: "/services/farm-management",
            color: "bg-cyan-500"
        },
        {
            title: "Precision & Protected Farming",
            desc: "Greenhouse & polyhouse guidance, precision input application, and high-value crop production.",
            icon: <SparklesIcon className="w-8 h-8" />,
            link: "/services/precision-agriculture",
            color: "bg-purple-500"
        },
        {
            title: "Organic & Sustainable Farming",
            desc: "Organic farming systems, bio-fertilizers, reduced chemical dependency, and regenerative practices.",
            icon: <GlobeAltIcon className="w-8 h-8" />,
            link: "/services/expert-consultancy",
            color: "bg-emerald-500"
        },
        {
            title: "Hydroponics & Soilless Farming",
            desc: "System design, nutrient solution management, and high-density production models for urban farming.",
            icon: <BeakerIcon className="w-8 h-8" />,
            link: "/services/hydroponics",
            color: "bg-teal-500"
        },
        {
            title: "Post-Harvest & Value Addition",
            desc: "Harvest timing optimization, storage improvement, grading, packaging, and market-ready solutions.",
            icon: <ArchiveBoxIcon className="w-8 h-8" />,
            link: "/contact",
            color: "bg-orange-500"
        }
    ];

    return (
        <div className="bg-gray-50 min-h-screen font-sans">
            <SEO
                title="Agricultural Services - Soil Testing, Crop Advisory & Consultancy"
                description="Expert agricultural consultancy services including soil testing, crop advisory, precision farming, and farm management. Improve your yield today."
                keywords="Agriculture Consulting, Soil Testing Lab, Crop Advisory, Farm Management Services, Agronomy Services"
            />
            {/* Header Banner - BigHaat Style */}
            <section className="bg-gradient-to-r from-green-600 to-green-700 text-white py-12 md:py-16">
                <div className="container mx-auto px-6">
                    <nav className="text-sm mb-4">
                        <Link to="/" className="text-green-200 hover:text-white">Home</Link>
                        <span className="mx-2">/</span>
                        <span>Our Services</span>
                    </nav>
                    <h1 className="text-3xl md:text-4xl font-bold">Our Solutions</h1>
                    <p className="text-green-100 mt-2 max-w-2xl">Comprehensive methodologies transforming every stage of the agricultural lifecycle</p>
                </div>
            </section>

            {/* Services Grid */}
            <section className="py-10">
                <div className="container mx-auto px-6 max-w-7xl">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {servicesList.map((service, idx) => (
                            <motion.div
                                key={idx}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                                variants={fadeInUp}
                                className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all border border-gray-100 group"
                            >
                                <div className={`w-14 h-14 ${service.color} rounded-xl flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform`}>
                                    {service.icon}
                                </div>
                                <h3 className="text-lg font-bold text-gray-900 mb-2">{service.title}</h3>
                                <p className="text-gray-600 text-sm mb-4 leading-relaxed">{service.desc}</p>
                                <Link to={service.link} className="inline-flex items-center gap-1 text-green-600 font-semibold text-sm hover:text-green-700 transition">
                                    Learn More <ArrowRightIcon className="w-4 h-4" />
                                </Link>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-10 px-6">
                <div className="container mx-auto max-w-4xl">
                    <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-2xl p-8 md:p-12 text-center text-white">
                        <h2 className="text-2xl md:text-3xl font-bold mb-4">Need a Customized Solution?</h2>
                        <p className="text-green-100 mb-6 max-w-xl mx-auto">
                            Every farm is unique. Contact us to design a tailored plan for your specific crop and soil conditions.
                        </p>
                        <Link to="/contact" className="inline-block bg-white text-green-700 px-8 py-3 rounded-lg font-bold shadow-lg hover:bg-gray-100 transition">
                            Get a Free Consultation
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Services;
